import { Link } from "react-router-dom";
import {
  ArrowRight, MessageSquareText, ClipboardCheck, Calculator, FileText,
  RefreshCw, CheckCircle2, CalendarCheck, Receipt, CreditCard, Plane, Flag,
  Building2, Users, BrainCircuit, UserCheck, Layers3, ShieldCheck, Sparkles, TrendingUp,
} from "lucide-react";
import { useI18n } from "@/context/I18nContext";

// Content lives in I18nContext (l_* keys) — these arrays reference translation keys only.
const STAGES = [
  ["01", "l_st1", MessageSquareText], ["02", "l_st2", ClipboardCheck],
  ["03", "l_st3", Calculator], ["04", "l_st4", FileText],
  ["05", "l_st5", RefreshCw], ["06", "l_st6", CheckCircle2],
  ["07", "l_st7", CalendarCheck], ["08", "l_st8", Receipt],
  ["09", "l_st9", CreditCard], ["10", "l_st10", Plane], ["11", "l_st11", Flag],
];

const PROBLEMS = [["l_p1_t", "l_p1_d"], ["l_p2_t", "l_p2_d"], ["l_p3_t", "l_p3_d"]];

const CAPABILITIES = [
  ["l_c1_t", "l_c1_d"], ["l_c2_t", "l_c2_d"], ["l_c3_t", "l_c3_d"],
  ["l_c4_t", "l_c4_d"], ["l_c5_t", "l_c5_d"], ["l_c6_t", "l_c6_d"],
  ["l_c7_t", "l_c7_d"], ["l_c8_t", "l_c8_d"], ["l_c9_t", "l_c9_d"],
  ["l_c10_t", "l_c10_d"],
];

const AI_POINTS = ["l_ai_b1", "l_ai_b2", "l_ai_b3", "l_ai_b4"];
const HI_POINTS = ["l_hi_b1", "l_hi_b2", "l_hi_b3", "l_hi_b4"];
const AUDIENCE = [
  ["l_a1_t", "l_a1_d", Building2], ["l_a2_t", "l_a2_d", TrendingUp],
  ["l_a3_t", "l_a3_d", Users], ["l_a4_t", "l_a4_d", Plane],
];

function LangSwitcher() {
  const { lang, setLang } = useI18n();
  return (
    <div className="flex items-center gap-1 rounded-lg border border-white/10 p-0.5" data-testid="landing-lang-switcher">
      {["en", "id"].map((l) => (
        <button key={l} type="button" onClick={() => setLang(l)} data-testid={`lang-switch-${l}`}
          aria-pressed={lang === l}
          className={`rounded-md px-2.5 py-1 text-[11px] font-bold tracking-wide transition-colors ${
            lang === l ? "bg-amber-400 text-[#0B1120]" : "text-slate-300 hover:text-white"}`}>
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

function TopBar() {
  const { t } = useI18n();
  return (
    <header className="sticky top-0 z-30 border-b border-white/10 bg-[#070F21]/85 backdrop-blur-md">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
        <div className="flex items-center gap-2.5">
          <img src="/brand/petrinos-p-symbol.png" alt="PETRINOS P-symbol" data-testid="nav-p-symbol" className="h-9 w-9 rounded-lg object-cover" />
          <div className="leading-tight">
            <div className="font-display text-base font-extrabold tracking-tight text-white">PETRINOS Travel OS</div>
            <div className="text-[10px] font-medium uppercase tracking-widest text-slate-400">Travel Operating System</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <LangSwitcher />
          <Link to="/login" data-testid="nav-sign-in" className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-200 hover:bg-white/10">{t("l_signin")}</Link>
          <Link to="/register" data-testid="nav-start-trial" className="flex items-center gap-1.5 rounded-lg bg-amber-400 px-3 py-2 text-xs font-semibold text-[#0B1120] hover:bg-amber-300 sm:px-4 sm:text-sm">
            {t("l_trial_cta")} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </header>
  );
}

export default function Landing() {
  const { t } = useI18n();
  return (
    <div className="min-h-screen bg-[#070F21] text-white" data-testid="landing-page">
      <TopBar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_15%_0%,rgba(212,175,55,0.08),transparent_40%),radial-gradient(circle_at_85%_25%,rgba(30,64,175,0.30),transparent_50%)]" />
        <div className="relative mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:py-24">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1 text-xs font-semibold text-amber-300">
                <Layers3 className="h-3.5 w-3.5" /> {t("l_hero_badge")}
              </span>
              <h1 className="mt-5 font-display text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl" data-testid="hero-headline">
                {t("l_hero_title_a")}<span className="text-amber-300">{t("l_hero_title_b")}</span>
              </h1>
              <p className="mt-3 text-base font-semibold uppercase tracking-wider text-slate-300">{t("l_hero_tagline")}</p>
              <p className="mt-3 max-w-xl text-base text-slate-400">{t("l_hero_sub")}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/register" data-testid="hero-start-trial" className="flex items-center gap-2 rounded-lg bg-amber-400 px-6 py-3 text-sm font-semibold text-[#0B1120] shadow-lg shadow-amber-400/20 hover:bg-amber-300">
                  {t("l_trial_cta")} <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/login" data-testid="hero-sign-in" className="rounded-lg border border-white/20 bg-white/5 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10">
                  {t("l_signin")}
                </Link>
              </div>
              <p className="mt-6 text-xs font-semibold uppercase tracking-widest text-slate-500">{t("l_hero_motto")}</p>
            </div>
            <div className="relative mx-auto w-full max-w-md">
              <div className="overflow-hidden rounded-2xl border border-white/10 shadow-2xl shadow-black/40">
                <img src="/brand/petrinos-lockup.webp" alt="PETRINOS Travel Operating System" data-testid="hero-lockup" className="w-full object-cover" />
              </div>
              <div className="absolute -bottom-4 -right-3 rounded-xl border border-amber-400/30 bg-[#0B1730] px-4 py-3 shadow-xl">
                <div className="flex items-center gap-2 text-xs font-semibold text-amber-300"><ShieldCheck className="h-4 w-4" /> {t("l_hero_ai_badge")}</div>
                <div className="mt-0.5 text-[10px] text-slate-400">{t("l_hero_ai_badge_sub")}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM / VALUE */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        <div className="mb-8">
          <p className="text-xs font-semibold uppercase tracking-wider text-amber-300">{t("l_prob_kicker")}</p>
          <h2 className="mt-1 font-display text-2xl font-bold sm:text-3xl">{t("l_prob_title")}</h2>
          <p className="mt-2 max-w-2xl text-sm text-slate-400">{t("l_prob_sub")}</p>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {PROBLEMS.map(([tk, dk]) => (
            <div key={tk} className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
              <h3 className="font-display font-semibold text-white">{t(tk)}</h3>
              <p className="mt-2 text-sm text-slate-400">{t(dk)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 11-STAGE WORKFLOW */}
      <section className="border-y border-white/10 bg-[#0A1428]">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
          <div className="mb-8">
            <p className="text-xs font-semibold uppercase tracking-wider text-amber-300">{t("l_wf_kicker")}</p>
            <h2 className="mt-1 font-display text-2xl font-bold sm:text-3xl">{t("l_wf_title")}</h2>
            <p className="mt-2 max-w-2xl text-sm text-slate-400">{t("l_wf_sub")}</p>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {STAGES.map(([n, lk, Icon]) => (
              <div key={n} data-testid={`landing-stage-${n}`} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] p-4 transition-colors hover:border-amber-400/40">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-400/10 text-amber-300"><Icon className="h-5 w-5" /></div>
                <div>
                  <div className="font-mono-tf text-[11px] text-slate-500">{n}</div>
                  <div className="font-semibold text-slate-100">{t(lk)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HUMAN + AI */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        <div className="overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[#0B1730] to-[#070F21]">
          <div className="grid gap-0 md:grid-cols-2">
            <div className="border-b border-white/10 p-8 md:border-b-0 md:border-r sm:p-10">
              <div className="flex items-center gap-2 text-sky-300"><BrainCircuit className="h-6 w-6" /><span className="text-xs font-semibold uppercase tracking-widest">{t("l_ai_kicker")}</span></div>
              <h3 className="mt-3 font-display text-xl font-bold">{t("l_ai_title")}</h3>
              <p className="mt-2 text-sm text-slate-400">{t("l_ai_desc")}</p>
              <ul className="mt-4 space-y-1.5 text-xs text-slate-300">
                {AI_POINTS.map((k) => (
                  <li key={k} className="flex items-center gap-2"><Sparkles className="h-3.5 w-3.5 text-sky-300" />{t(k)}</li>
                ))}
              </ul>
            </div>
            <div className="p-8 sm:p-10">
              <div className="flex items-center gap-2 text-amber-300"><UserCheck className="h-6 w-6" /><span className="text-xs font-semibold uppercase tracking-widest">{t("l_hi_kicker")}</span></div>
              <h3 className="mt-3 font-display text-xl font-bold">{t("l_hi_title")}</h3>
              <p className="mt-2 text-sm text-slate-400">{t("l_hi_desc")}</p>
              <ul className="mt-4 space-y-1.5 text-xs text-slate-300">
                {HI_POINTS.map((k) => (
                  <li key={k} className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-amber-300" />{t(k)}</li>
                ))}
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 px-8 py-4 text-center text-sm font-semibold text-slate-200">
            {t("l_ai_banner_a")}<span className="text-amber-300">{t("l_ai_banner_b")}</span>
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        <div className="mb-8">
          <p className="text-xs font-semibold uppercase tracking-wider text-amber-300">{t("l_cap_kicker")}</p>
          <h2 className="mt-1 font-display text-2xl font-bold sm:text-3xl">{t("l_cap_title")}</h2>
          <p className="mt-2 max-w-2xl text-sm text-slate-400">{t("l_cap_sub")}</p>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {CAPABILITIES.map(([tk, dk]) => (
            <div key={tk} className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
              <h3 className="font-display font-semibold text-white">{t(tk)}</h3>
              <p className="mt-2 text-sm text-slate-400">{t(dk)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* AUDIENCE */}
      <section className="border-t border-white/10 bg-[#0A1428]">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
          <div className="mb-8">
            <p className="text-xs font-semibold uppercase tracking-wider text-amber-300">{t("l_aud_kicker")}</p>
            <h2 className="mt-1 font-display text-2xl font-bold sm:text-3xl">{t("l_aud_title")}</h2>
            <p className="mt-2 max-w-2xl text-sm text-slate-400">{t("l_aud_sub")}</p>
          </div>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
            {AUDIENCE.map(([tk, dk, Icon]) => (
              <div key={tk} className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-400/10 text-amber-300"><Icon className="h-5 w-5" /></div>
                <h3 className="mt-3 font-display font-semibold text-white">{t(tk)}</h3>
                <p className="mt-2 text-sm text-slate-400">{t(dk)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden border-t border-white/10">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(212,175,55,0.15),transparent_55%)]" />
        <div className="relative mx-auto max-w-4xl px-4 py-20 text-center sm:px-6">
          <img src="/brand/petrinos-p-symbol.png" alt="PETRINOS" className="mx-auto h-14 w-14 rounded-xl object-cover" />
          <h2 className="mt-5 font-display text-3xl font-extrabold sm:text-4xl">{t("l_cta_title")}</h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-slate-400">{t("l_cta_sub")}</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/register" data-testid="cta-start-trial" className="inline-flex items-center gap-2 rounded-lg bg-amber-400 px-8 py-3.5 text-sm font-semibold text-[#0B1120] hover:bg-amber-300">
              {t("l_trial_cta")} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/login" data-testid="cta-sign-in" className="rounded-lg border border-white/20 bg-white/5 px-8 py-3.5 text-sm font-semibold text-white hover:bg-white/10">{t("l_signin")}</Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-2 px-4 py-6 text-xs text-slate-500 sm:flex-row sm:px-6">
          <div data-testid="footer-left">{t("l_footer_left")}</div>
          <div data-testid="footer-right">{t("l_footer_right")}</div>
        </div>
      </footer>
    </div>
  );
}
